import {Close as CloseIcon, Label as LabelIcon} from '@mui/icons-material';
import {
  Button,
  Chip,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {FC, useCallback, useState, useMemo} from 'react';
import {styled} from 'styled-components';

import {useDbData} from '@website_frontend/lib/db_cache';
import {
  getItemTags,
  addTagToItem,
  removeTagFromItem,
  useAllTags,
} from '@website_frontend/lib/user_data_storage';

interface TagManagementSheetProps {
  open: boolean;
  onClose: () => void;
  itemId: number | null;
}

export const TagManagementSheet: FC<TagManagementSheetProps> = ({open, onClose, itemId}) => {
  const {data} = useDbData();
  const item = useMemo(() => (itemId ? data?.get(itemId) : undefined), [data, itemId]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [newTagName, setNewTagName] = useState('');
  const allTags = useAllTags();

  const itemTags = useMemo(() => {
    if (!itemId) return [];
    return getItemTags(itemId);
  }, [itemId]);

  const availableTags = useMemo(() => {
    return allTags.filter(tag => !itemTags.includes(tag));
  }, [allTags, itemTags]);

  const handleAddTag = useCallback(
    (tag: string) => {
      if (!itemId) return;
      addTagToItem(itemId, tag);
    },
    [itemId]
  );

  const handleRemoveTag = useCallback(
    (tag: string) => {
      if (!itemId) return;
      removeTagFromItem(itemId, tag);
    },
    [itemId]
  );

  const handleCreateNewTag = useCallback(() => {
    if (!(newTagName.trim() && itemId)) return;
    const tag = newTagName.trim();
    if (!itemTags.includes(tag)) {
      handleAddTag(tag);
      setNewTagName('');
    }
  }, [newTagName, itemId, itemTags, handleAddTag]);

  if (!itemId) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          margin: 0,
          maxHeight: isMobile ? '100%' : '80vh',
          width: isMobile ? '100%' : '100%',
          maxWidth: isMobile ? '100%' : 600,
          borderRadius: isMobile ? 0 : '16px 16px 0 0',
        },
      }}
      TransitionProps={{
        // Slide up animation
        style: {
          transition: 'transform 0.3s ease-out',
        },
      }}
    >
      <SheetContent>
        <Header>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LabelIcon />
            <Typography variant="h6">Gérer les tags</Typography>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Header>

        <ItemInfo>
          <Typography variant="subtitle2" color="text.secondary" noWrap>
            {item?.title}
          </Typography>
        </ItemInfo>

        <Stack spacing={2}>
          {/* Current tags */}
          {itemTags.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Tags actuels
              </Typography>
              <TagList>
                {itemTags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    color="primary"
                    variant="filled"
                  />
                ))}
              </TagList>
            </Stack>
          )}

          {/* Add existing tag */}
          {availableTags.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Ajouter un tag existant
              </Typography>
              <TagList>
                {availableTags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    onClick={() => handleAddTag(tag)}
                    variant="outlined"
                    clickable
                  />
                ))}
              </TagList>
            </Stack>
          )}

          {/* Create new tag */}
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Créer un nouveau tag
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                value={newTagName}
                onChange={e => setNewTagName(e.target.value)}
                placeholder="Nom du tag"
                size="small"
                fullWidth
                onKeyDown={e => {
                  if (e.key === 'Enter' && newTagName.trim()) {
                    handleCreateNewTag();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handleCreateNewTag}
                disabled={!newTagName.trim() || itemTags.includes(newTagName.trim())}
              >
                Ajouter
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </SheetContent>
    </Dialog>
  );
};

TagManagementSheet.displayName = 'TagManagementSheet';

const SheetContent = styled.div`
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
  background-color: white;
  border-radius: 16px 16px 0 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const ItemInfo = styled.div`
  margin-bottom: 24px;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 8px;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
